import Vue from 'vue'
import ElementUI from 'element-ui'
import elLocale from 'element-ui/lib/locale'
import elLangTW from 'element-ui/lib/locale/lang/zh-TW'
import 'element-ui/lib/theme-chalk/index.css'

import App from './App.vue'
import router from './router'
import store from './store'
import './styles/common.css'
import { thumbUrl } from './utils/file'
import { MAX_UPLOAD_MB } from './constants/upload'
import { twOptionLabel } from './constants/imageOptions'
import { mentionName, resolveMentions } from './utils/mention'
import MentionEditor from './components/MentionEditor.vue'

// Element UI 内置文案使用台湾繁体（分页、日期选择器、确认框等）
elLocale.use(elLangTW)
Vue.use(ElementUI, { size: 'small' })
Vue.config.productionTip = false

/**
 * 大图预览「到头即止」：首/尾时对应箭头置灰（而不是隐藏）。
 *
 * Element UI 的 image-viewer 本来就支持这个行为 —— 模板里 `!infinite && isFirst/isLast`
 * 会给箭头加 `is-disabled` 类，prev/next 也会直接 return。
 * 问题在于它的 `infinite` **是 data 里的初值、硬编码为 true**（不是 prop！），
 * 而且 el-image 内部只传了 z-index / initial-index / on-close / url-list，
 * 外部完全没法关掉循环。
 *
 * 所以这里包一层它的 data 函数，把 infinite 初值改成 false。
 * 必须用 `origData.call(this)` —— 原 data 里 `index: this.initialIndex` 依赖 this。
 * 找不到内部组件时只打警告，不影响页面（Element UI 升级改了结构也不会炸）。
 * 置灰的视觉样式在 styles/common.css 里补（Element 只加类、没写样式）。
 */
try {
  const registered = Vue.options.components || {}
  const ElImage = registered.ElImage || registered['el-image']
  const Viewer = ElImage && ElImage.components && ElImage.components.ImageViewer
  const origData = Viewer && Viewer.data
  if (typeof origData === 'function') {
    Viewer.data = function patchedData() {
      const d = origData.call(this) || {}
      d.infinite = false
      return d
    }
  } else {
    console.warn('[ece] 未能关闭 el-image 预览循环（找不到 ImageViewer.data，Element UI 内部结构可能变了）')
  }
} catch (e) {
  console.warn('[ece] 关闭 el-image 预览循环失败:', e)
}

/**
 * 缩略图 URL 生成器（小图预览统一走它，别直接渲染原图）。
 *
 * 挂在原型上，组件模板/JS 里可直接用 $thumb(path, 宽度)，
 * 省去每个组件重复 import + 注册 methods。实现见 utils/file.js 的 thumbUrl。
 */
Vue.prototype.$thumb = thumbUrl

/**
 * 上传大小上限（MB）。各上传入口用它做前端预检，省得用户白传几十 MB；
 * 真正的拦截在后端（超限返回 413 并给出明确文案）。
 * 前端 / 后端 upload.max.mb / nginx client_max_body_size 三处需保持一致。
 */
Vue.prototype.$maxUploadMb = MAX_UPLOAD_MB

/**
 * 选项显示名的台湾繁体（如风格「简约」界面显示「簡約」）。
 * 这些选项既是界面文字又是提交给后端的参数值，所以只转显示、提交值保持原样。
 * 映射表见 constants/imageOptions.js 的 OPTION_LABELS_TW。
 */
Vue.prototype.$twOption = twOptionLabel

/**
 * @ 引用素材：`$mentionName(item, idx)` 取显示名、`$resolveMentions(text, sources, format)`
 * 把提交前的 `@素材名` 换成记号（默认 `[参考图N]`；视频侧传 format 换成 Seedance 官方的
 * `<图片N>`）。N 与传给模型的参考图数组序号一致。
 *
 * 挂原型是为了让编辑器里的 chip 名与替换规则**共用同一份实现** ——
 * 两边不一致会出现「chip 叫 A、替换时找 B」，提示词里的编号就指错图了。
 */
Vue.prototype.$mentionName = mentionName
Vue.prototype.$resolveMentions = resolveMentions

/**
 * @ 引用输入框（文字与素材 chip 混排）：全局注册，工作台直接写 <MentionEditor> 即可，
 * 不必每个组件都 import + 注册一遍（首页目前是自实现的一套，逐步收敛到这一个）。
 */
Vue.component('MentionEditor', MentionEditor)

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
